import {
  APICurrentUser,
  APIPresentationAssetAttributes,
} from '@ardoq/api-types';
import { I18nLocale, t } from '@ardoq/i18n';
import { IconName } from '@ardoq/icons';
import {
  ActionMenuIconButton,
  toTopBarIconButtonProps,
} from '@ardoq/page-layout';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  shareReplay,
} from 'rxjs';
import { currentUser$ } from 'streams/currentUser/currentUser$';
import { contextPresentation$ } from 'streams/presentations/contextPresentation$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { presentationsOverview$ } from 'presentation/presentations2024/presentationsOverview$';
import {
  getCanAccessPresentationEditor,
  makeTogglePresentationEditorHandler,
} from './presentationEditorToggle';
import { isShowingNewPresentationForm } from './presentationSidebar/presentationSidebarOperations';
import { selectedModule$ } from './selectedModule$';
import { togglePresentationEditor$ } from './togglePresentationEditor$';
import { isPresentationSupportedOnPage$ } from './presentationSidebar/isPresentationSupportedOnPage$';
import { AppModules } from './types';

export type PresentationEditorToggle = {
  canAccess: boolean;
  isOpen: boolean;
  label: string;
  onToggle: VoidFunction;
};

type PresentationEditorToggleSources = {
  currentUser: APICurrentUser | null;
  presentation: APIPresentationAssetAttributes | undefined;
  presentationToggled: boolean;
  isCreatingNewPresentation: boolean;
  selectedModule: AppModules;
  isPresentationSupportedOnPage: boolean;
  i18nLocale: I18nLocale;
};

export const resolveIsOpen = ({
  presentation,
  presentationToggled,
  isCreatingNewPresentation,
  selectedModule,
}: Pick<
  PresentationEditorToggleSources,
  | 'presentation'
  | 'presentationToggled'
  | 'isCreatingNewPresentation'
  | 'selectedModule'
>): boolean =>
  presentationToggled &&
  (presentation !== undefined ||
    isShowingNewPresentationForm({
      selectedModule,
      isCreatingNewPresentation,
    }));

export const toPresentationEditorToggle = ({
  currentUser,
  presentation,
  presentationToggled,
  isCreatingNewPresentation,
  selectedModule,
  isPresentationSupportedOnPage,
  i18nLocale,
}: PresentationEditorToggleSources): PresentationEditorToggle => {
  const isOpen =
    isPresentationSupportedOnPage &&
    resolveIsOpen({
      presentation,
      presentationToggled,
      isCreatingNewPresentation,
      selectedModule,
    });
  return {
    canAccess:
      isPresentationSupportedOnPage &&
      getCanAccessPresentationEditor(currentUser),
    isOpen,
    label: t(
      i18nLocale,
      'appContainer:presentationEditorToggle.label',
      'Presentation editor'
    ),
    onToggle: makeTogglePresentationEditorHandler({
      presentation,
      isOpen,
      selectedModule,
    }),
  };
};

export const toPresentationEditorToggleActionItem = ({
  canAccess,
  isOpen,
  label,
  onToggle,
}: PresentationEditorToggle): ActionMenuIconButton | null => {
  if (!canAccess) return null;

  return toTopBarIconButtonProps({
    label,
    icon: { name: IconName.PRESENTATION },
    state: isOpen ? 'active' : 'idle',
    onClick: onToggle,
  });
};

const isCreatingNewPresentation$: Observable<boolean> =
  presentationsOverview$.pipe(
    map(({ isCreatingNewPresentation }) => isCreatingNewPresentation),
    distinctUntilChanged()
  );

export const presentationEditorToggle$: Observable<PresentationEditorToggle> =
  combineLatest([
    currentUser$,
    contextPresentation$,
    togglePresentationEditor$,
    isCreatingNewPresentation$,
    selectedModule$,
    isPresentationSupportedOnPage$,
    i18nLocale$,
  ]).pipe(
    map(
      ([
        currentUser,
        presentation,
        presentationToggled,
        isCreatingNewPresentation,
        { selectedModule },
        isPresentationSupportedOnPage,
        i18nLocale,
      ]) =>
        toPresentationEditorToggle({
          currentUser,
          presentation,
          presentationToggled,
          isCreatingNewPresentation,
          selectedModule,
          isPresentationSupportedOnPage,
          i18nLocale,
        })
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

export const presentationEditorToggleActionItem$: Observable<ActionMenuIconButton | null> =
  presentationEditorToggle$.pipe(map(toPresentationEditorToggleActionItem));
